/**
 * Pure helpers for the format part of the one-off work specification in the public
 * registration form. Mirrors the internal form of ui-br-publishers (RECEK-74182): one
 * publication form with exactly one refining format, so a request always yields one product.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
export var PUBLICATION_FORMS = [
    "PRINT",
    "EBOOK",
    "DATA_CARRIER",
    "AUDIOBOOK",
];
/** Error type of mod-br-publishers for a work specification with more than one product format. */
export var MULTIPLE_WORK_SPECIFICATION_FORMATS_ERROR_TYPE = "-13";
var isPublicationForm = function (value) {
    return !!value && PUBLICATION_FORMS.includes(value);
};
/** The refining format values stored for a publication form. */
export var formatsOfPublicationForm = function (workSpecification, publicationForm) {
    var _a, _b, _c, _d, _e;
    if (!workSpecification)
        return [];
    switch (publicationForm) {
        case "PRINT":
            return (_b = (_a = workSpecification.publicationType) === null || _a === void 0 ? void 0 : _a.printedPublications) !== null && _b !== void 0 ? _b : [];
        case "EBOOK":
            return (_c = workSpecification.eBookFormats) !== null && _c !== void 0 ? _c : [];
        case "DATA_CARRIER":
            return (_d = workSpecification.dataCarrierVersions) !== null && _d !== void 0 ? _d : [];
        case "AUDIOBOOK":
            return (_e = workSpecification.audioBookFormats) !== null && _e !== void 0 ? _e : [];
        default:
            return [];
    }
};
/** Number of product formats requested; the free-text "Jiný formát" rows are not counted. */
export var countProductFormats = function (workSpecification) {
    return PUBLICATION_FORMS.reduce(function (count, form) {
        return count + formatsOfPublicationForm(workSpecification, form).length;
    }, 0);
};
/**
 * The format fields after choosing a publication form: the chosen form keeps at most its first
 * refining value, every other form is cleared, and the "Jiný formát" rows are kept for an
 * e-book only — so the payload always carries at most one format.
 */
export var formatFieldsForPublicationForm = function (workSpecification, publicationForm) {
    var _a;
    var keep = function (form) {
        return publicationForm === form
            ? formatsOfPublicationForm(workSpecification, form).slice(0, 1)
            : [];
    };
    return {
        publicationForm: publicationForm,
        publicationType: { printedPublications: keep("PRINT") },
        eBookFormats: keep("EBOOK"),
        otherFormats: publicationForm === "EBOOK"
            ? ((_a = workSpecification === null || workSpecification === void 0 ? void 0 : workSpecification.otherFormats) !== null && _a !== void 0 ? _a : []).map(function (row) {
                var _a;
                return ({
                    format: (_a = row === null || row === void 0 ? void 0 : row.format) !== null && _a !== void 0 ? _a : "",
                });
            })
            : [],
        dataCarrierVersions: keep("DATA_CARRIER"),
        audioBookFormats: keep("AUDIOBOOK"),
    };
};
/**
 * The format fields after choosing a refining value of a publication form: the form carries
 * only that value (nothing when the select is reset), every other form is cleared.
 */
export var formatFieldsForSelection = function (workSpecification, publicationForm, value) {
    var selected = value ? [value] : [];
    var sources = {
        PRINT: { publicationType: { printedPublications: selected } },
        EBOOK: { eBookFormats: selected },
        DATA_CARRIER: { dataCarrierVersions: selected },
        AUDIOBOOK: { audioBookFormats: selected },
    };
    return formatFieldsForPublicationForm(__assign(__assign({}, sources[publicationForm]), { otherFormats: workSpecification === null || workSpecification === void 0 ? void 0 : workSpecification.otherFormats }), publicationForm);
};
/** Form field path and validation message of the refining select of each publication form. */
export var REFINING_FORMAT_FIELDS = {
    PRINT: {
        fieldPath: "workSpecification.publicationType.printedPublications",
        messageId: "stripes-core.publishers.validation.workSpec.printedPublications",
    },
    EBOOK: {
        fieldPath: "workSpecification.eBookFormats",
        messageId: "stripes-core.publishers.validation.workSpec.eBookFormats",
    },
    DATA_CARRIER: {
        fieldPath: "workSpecification.dataCarrierVersions",
        messageId: "stripes-core.publishers.validation.workSpec.dataCarrierVersions",
    },
    AUDIOBOOK: {
        fieldPath: "workSpecification.audioBookFormats",
        messageId: "stripes-core.publishers.validation.workSpec.audioBookFormats",
    },
};
/**
 * The refining select that is required but empty for the chosen publication form, or null
 * when no form is chosen or the form has its format.
 */
export var missingRefiningFormat = function (workSpecification) {
    var form = workSpecification === null || workSpecification === void 0 ? void 0 : workSpecification.publicationForm;
    if (!isPublicationForm(form))
        return null;
    return formatsOfPublicationForm(workSpecification, form).length > 0
        ? null
        : REFINING_FORMAT_FIELDS[form];
};
/**
 * Whether a failed save was rejected by mod-br-publishers because the work specification asks
 * for more than one product format (HTTP 422, error type "-13").
 *
 * @param error the error thrown by the ky client (an HTTPError carrying the response)
 */
export var isMultipleWorkSpecFormatsError = function (error) { return __awaiter(void 0, void 0, void 0, function () {
    var response, body, _a;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                response = error === null || error === void 0 ? void 0 : error.response;
                if (!response || typeof response.clone !== "function")
                    return [2 /*return*/, false];
                _b.label = 1;
            case 1:
                _b.trys.push([1, 3, , 4]);
                return [4 /*yield*/, response.clone().json()];
            case 2:
                body = _b.sent();
                return [2 /*return*/, (Array.isArray(body === null || body === void 0 ? void 0 : body.errors) &&
                        body.errors.some(function (item) {
                            return (item === null || item === void 0 ? void 0 : item.type) === MULTIPLE_WORK_SPECIFICATION_FORMATS_ERROR_TYPE;
                        }))];
            case 3:
                _a = _b.sent();
                return [2 /*return*/, false];
            case 4: return [2 /*return*/];
        }
    });
}); };
